// Route `api` cho `finArCustomer` (L-DT): `/v1/finance/ar` (INVOUT, SQL 018, chặn quyền tiền) → công nợ theo khách.
// DB không có hạn mức / DSO / liên hệ / hạn thanh toán: hạn mức = 0 (chưa có), DSO = 0, liên hệ rỗng; hạn = ngày debit + 30 ngày (mặc định, không bịa quá hạn riêng từng khách).
// Tiền đổi sang triệu đồng như board. Gom theo khách trên trang đã lấy (route chưa có tổng hợp phía worker → luồng A).
import type { ApiRoute } from '@hana/data';
import type { ArCustomer, IssuedInvoice, Receipt } from '../finance/types';
import { dayLabel } from '../finance/apiRoute';

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const tr = (v: unknown): number => Math.round((num(v) / 1e6) * 100) / 100;
const DAY = 86_400_000;
/** Hạn mặc định tính từ ngày debit (DB chưa có điều khoản thanh toán). */
export const DEFAULT_TERM_DAYS = 30;

/** Còn phải thu của một debit / credit (triệu): đã thu xong hoặc huỷ → 0; credit note (CN) âm. */
export function openAmount(r: Row): number {
  const st = str(r.PMSTT);
  if (st === 'Done' || st === 'Cancelled') return 0;
  const left = tr(r.TotalRevenue) - tr(r.PMAmount);
  return Math.round((str(r.TypeDoc) === 'CN' ? -Math.abs(left) : left) * 100) / 100;
}

/** Số ngày quá hạn tại `now` (≤ 0 = chưa đến hạn); ngày hỏng → chưa đến hạn. */
export function overdueDays(r: Row, now: number): number {
  const t = Date.parse(str(r.DebitDate).slice(0, 10));
  return Number.isNaN(t) ? 0 : Math.floor((now - t) / DAY) - DEFAULT_TERM_DAYS;
}

export function arCustomersFromApi(rows: readonly Row[], now: number = Date.now()): ArCustomer[] {
  const by = new Map<string, ArCustomer>();
  for (const r of rows) {
    const name = str(r.ShortName);
    const amt = openAmount(r);
    if (!name || amt === 0) continue;
    const c = by.get(name) ?? { customer: name, sale: str(r.SaleName), limit: 0, current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90: 0, dso: 0, contact: '', tel: '' };
    const d = overdueDays(r, now);
    const k = d <= 0 ? 'current' : d <= 30 ? 'd1_30' : d <= 60 ? 'd31_60' : d <= 90 ? 'd61_90' : 'd90';
    c[k] = Math.round((c[k] + amt) * 100) / 100;
    by.set(name, c);
  }
  return [...by.values()].filter((c) => c.current + c.d1_30 + c.d31_60 + c.d61_90 + c.d90 > 0).sort((a, b) => b.current + b.d1_30 + b.d31_60 + b.d61_90 + b.d90 - (a.current + a.d1_30 + a.d31_60 + a.d61_90 + a.d90));
}

const items = (json: unknown): Row[] => {
  const list = (json as { items?: unknown } | null)?.items;
  return Array.isArray(list) ? (list as Row[]) : [];
};

export const arCustomerApiRoute: ApiRoute<ArCustomer> = {
  path: '/api/v1/finance/ar',
  // công nợ = debit chưa thu xong; worker trả tối đa 100 dòng / trang
  query: () => ({ limit: 100, page: 1, pm: 'Pending' }),
  parse: (json) => {
    const list = arCustomersFromApi(items(json));
    return { items: list, total: list.length, hasMore: false };
  },
};

/**
 * Hoá đơn bán ra đã xuất ← INVOUT (cùng `/v1/finance/ar`): gom các debit cùng `InvoNo` thành một hoá đơn, tiền hàng = Revenue, VAT = TotalVat (triệu).
 * DB không có trạng thái ký số / gửi khách: để rỗng, không bịa. Huỷ (`InvoiceStatus` Cancelled) bỏ qua.
 */
export function issuedInvoicesFromApi(rows: readonly Row[]): IssuedInvoice[] {
  const by = new Map<string, IssuedInvoice>();
  for (const r of rows) {
    const no = str(r.InvoNo);
    if (!no || /^cancel/i.test(str(r.InvoiceStatus))) continue;
    const cur = by.get(no) ?? { no, date: dayLabel(r.InvDate), customer: str(r.ShortName), net: 0, vat: 0, signed: '', sent: '', period: Number(str(r.InvDate).slice(5, 7)) || 0 };
    cur.net = Math.round((cur.net + tr(r.Revenue)) * 100) / 100;
    cur.vat = Math.round((cur.vat + tr(r.TotalVat)) * 100) / 100;
    by.set(no, cur);
  }
  return [...by.values()];
}

export const issuedInvoiceApiRoute: ApiRoute<IssuedInvoice> = {
  path: '/api/v1/finance/ar',
  query: () => ({ limit: 100, page: 1 }),
  parse: (json) => {
    const list = issuedInvoicesFromApi(items(json));
    return { items: list, total: list.length, hasMore: false };
  },
};

/**
 * Phiếu thu ← `Payment` (`/v1/finance/payments`). Tiền = TOTALREVENUE (triệu đồng); quỹ USD → tiền tệ USD.
 * Query payments chưa trả dòng gắn debit / HĐ (links rỗng) và người duyệt: không bịa.
 */
export function receiptsFromApi(rows: readonly Row[]): Receipt[] {
  return rows
    .filter((r) => str(r.REF))
    .map((r): Receipt => ({
      no: str(r.REF), date: dayLabel(r.DATE), customer: str(r.ShortName), bank: str(r.Account), amount: tr(r.TOTALREVENUE),
      currency: /usd/i.test(str(r.Account)) ? 'USD' : 'VND', links: [], memo: str(r.NOTE), maker: str(r.USER), approved: r.Approved === true || r.Approved === 1 || str(r.Approved).toLowerCase() === 'true',
    }));
}

export const receiptApiRoute: ApiRoute<Receipt> = {
  path: '/api/v1/finance/payments',
  query: () => ({ limit: 100, page: 1 }),
  parse: (json) => {
    const list = receiptsFromApi(items(json));
    return { items: list, total: list.length, hasMore: false };
  },
};
